import { mixpanelService } from '../analytics/mixpanelService';
import { createFileLogger } from '../../utils/logger';
import { getUserDemographics, getUserDemographicsForProfile } from '../../utils/userDemographics';
import { isLocalhost } from '../../config/mixpanel';

const log = createFileLogger('USERTRACKINGSERVICE_SERVICE', { enabled: false });

interface UserAnalyticsProfile {
  anonymousId: string;
  userId?: string;
  sessionId: string;
  templates: {
    [templateId: string]: {
      accessCount: number;
      runCount: number;
      avgRefinements: number;
      avgCompletionTime: number;
      lastUsed: string;
    }
  };
  preferences: {
    favoriteCategories: string[];
    complexityPreference?: string;
    avgSessionLength: number;
  };
  createdAt: string;
  lastActiveAt: string;
}

class UserTrackingService {
  private anonymousId: string | null = null;
  private userId: string | null = null;
  private sessionId: string | null = null;
  private isInitialized = false;
  private log = createFileLogger('USER_TRACKING');

  /**
   * Initialize user tracking
   */
  init(): void {
    if (this.isInitialized) {
      this.log.debug('User tracking already initialized');
      return;
    }

    // Block all user tracking on localhost
    if (isLocalhost()) {
      this.log.debug('[LOCALHOST] User tracking disabled for localhost');
      this.isInitialized = true; // Mark as initialized but don't do anything
      return;
    }

    // Get or create anonymous ID
    this.anonymousId = localStorage.getItem('galos_anonymous_id') || this.generateUUID();
    localStorage.setItem('galos_anonymous_id', this.anonymousId);
    
    // Generate session ID
    this.sessionId = this.generateUUID();
    
    // Check if user is authenticated
    const authToken = localStorage.getItem('authToken');
    if (authToken) {
      // Try to extract user ID from token or get from another source
      this.userId = localStorage.getItem('userId') || null;
    }
    
    // Initialize Mixpanel with current user (if Mixpanel is ready)
    const identityId = this.userId || this.anonymousId;
    try {
      mixpanelService.identify(identityId);
    } catch (error) {
      this.log.debug('Mixpanel not ready during userTrackingService init, will retry later');
    }
    
    // Collect comprehensive demographic data
    const demographics = getUserDemographicsForProfile();
    const fullDemographics = getUserDemographics();
    
    // Set user properties with comprehensive demographic data
    const userProperties = {
      // Basic User Info
      $name: this.userId || `Anonymous User`,
      $email: undefined, // Will be set later when user authenticates
      anonymous_id: this.anonymousId,
      session_id: this.sessionId,
      user_type: this.userId ? 'authenticated' : 'anonymous',
      initialized_at: new Date().toISOString(),
      
      // Device & Technical Demographics (appropriate for user profile)
      device_type: demographics.device_type,
      screen_resolution: demographics.screen_resolution,
      browser_name: demographics.browser_name,
      browser_version: fullDemographics.browser_version,
      os_name: demographics.os_name,
      os_version: fullDemographics.os_version,
      
      // Geographic & Locale Information
      timezone: demographics.timezone,
      timezone_offset: fullDemographics.timezone_offset,
      language: demographics.language,
      languages: fullDemographics.languages,
      currency: demographics.currency,
      
      // User Preferences & Accessibility
      color_scheme: demographics.color_scheme,
      reduced_motion: demographics.reduced_motion,
      touch_support: demographics.touch_support,
      
      // Technical Capabilities
      cookies_enabled: fullDemographics.cookies_enabled,
      local_storage_enabled: fullDemographics.local_storage_enabled,
      online: fullDemographics.online,
      pixel_ratio: fullDemographics.pixel_ratio,
      
      // Network & Performance (for replay quality insights)
      connection_type: fullDemographics.connection_type,
      connection_downlink: fullDemographics.connection_downlink,
      memory: fullDemographics.memory,
      
      // Session Context
      referrer: fullDemographics.referrer,
      entry_point: fullDemographics.entry_point,
      is_new_session: fullDemographics.is_new_session,
      
      // Viewport for replay analysis
      viewport_width: fullDemographics.viewport_width,
      viewport_height: fullDemographics.viewport_height,
    };
    
    try {
      mixpanelService.setUserProperties(userProperties);
    } catch (error) {
      this.log.debug('Mixpanel not ready for setting user properties, will retry later');
    }
    
    this.isInitialized = true;
    this.log.debug('User tracking initialized', {
      anonymousId: this.anonymousId,
      userId: this.userId ? '[REDACTED]' : null,
      sessionId: this.sessionId
    });
  }

  /**
   * Set authenticated user
   */
  setUser(userId: string): void {
    if (!this.isInitialized) {
      this.log.warn('User tracking not initialized. Call init() first.');
      return;
    }

    // Block all user tracking on localhost
    if (isLocalhost()) {
      this.log.debug('[LOCALHOST] User tracking disabled, skipping setUser');
      return;
    }

    const wasAnonymous = !this.userId;
    this.userId = userId;
    localStorage.setItem('userId', userId);
    
    // Get updated demographics for the authenticated user
    const demographics = getUserDemographicsForProfile();
    
    // Alias anonymous user to authenticated user in Mixpanel
    mixpanelService.identify(userId);
    mixpanelService.setUserProperties({
      $name: userId, // Will be updated with real name when available
      anonymous_id: this.anonymousId,
      user_type: 'authenticated',
      signup_date: wasAnonymous ? new Date().toISOString() : undefined,
      conversion_session: wasAnonymous ? this.sessionId : undefined,
      authenticated_at: new Date().toISOString(),
      
      // Update any demographics that might have changed
      ...demographics
    });
    
    // Track conversion event - DISABLED
    if (wasAnonymous) {
      this.log.debug('User conversion tracking disabled - no Mixpanel event sent');
      // User conversion tracking disabled - no Mixpanel event sent
    }
    
    this.log.debug('User set as authenticated', { userId, wasAnonymous });
  }

  /**
   * Get current user ID (authenticated or anonymous)
   */
  getCurrentUserId(): string {
    if (!this.isInitialized) {
      this.log.warn('User tracking not initialized. Call init() first.');
      return 'unknown';
    }
    return this.userId || this.anonymousId!;
  }

  /**
   * Get current session ID
   */
  getSessionId(): string {
    if (!this.isInitialized) {
      this.log.warn('User tracking not initialized. Call init() first.');
      return 'unknown';
    }
    return this.sessionId!;
  }

  /**
   * Get user type
   */
  getUserType(): 'authenticated' | 'anonymous' {
    return this.userId ? 'authenticated' : 'anonymous';
  }

  /**
   * Update user info when more details become available
   */
  updateUserInfo(userInfo: { 
    name?: string; 
    email?: string; 
    userId?: string;
    plan?: string;
    personalPlan?: string;
    isPaidUser?: boolean;
    ownsPaidOrg?: boolean;
    paidOrgsCount?: number;
  }): void {
    if (!this.isInitialized) {
      this.log.warn('User tracking not initialized. Call init() first.');
      return;
    }

    // 🔥 LOG INCOMING EMAIL
    this.log.info('🔍 [MIXPANEL EMAIL CHECK] updateUserInfo called with:', {
      hasEmail: !!userInfo.email,
      emailLength: userInfo.email?.length || 0,
      hasName: !!userInfo.name,
      hasUserId: !!userInfo.userId,
      emailIsEmpty: userInfo.email === '',
      plan: userInfo.plan,
      isPaidUser: userInfo.isPaidUser
    });

    const properties: Record<string, any> = {};
    
    if (userInfo.name) {
      properties.$name = userInfo.name;
    }
    
    if (userInfo.email) {
      properties.$email = userInfo.email;
      this.log.info('✅ [MIXPANEL EMAIL CHECK] Email will be sent to Mixpanel');
    } else {
      this.log.warn('⚠️ [MIXPANEL EMAIL CHECK] NO EMAIL in userInfo - Mixpanel will NOT get email!');
    }
    
    // Add plan properties
    if (userInfo.plan) {
      properties.plan = userInfo.plan;
    }
    if (userInfo.personalPlan) {
      properties.personal_plan = userInfo.personalPlan;
    }
    if (userInfo.isPaidUser !== undefined) {
      properties.is_paid_user = userInfo.isPaidUser;
    }
    if (userInfo.ownsPaidOrg !== undefined) {
      properties.owns_paid_org = userInfo.ownsPaidOrg;
    }
    if (userInfo.paidOrgsCount !== undefined) {
      properties.paid_orgs_count = userInfo.paidOrgsCount;
    }
    
    if (userInfo.userId && userInfo.userId !== this.userId) {
      this.setUser(userInfo.userId);
      return; // setUser will handle the rest
    }
    
    if (Object.keys(properties).length > 0) {
      // Include updated demographics when updating user info
      const demographics = getUserDemographicsForProfile();
      const updatedProperties = {
        ...properties,
        ...demographics,
        last_updated_at: new Date().toISOString()
      };
      
      this.log.info('📤 [MIXPANEL EMAIL CHECK] Calling mixpanelService.setUserProperties with:', {
        hasEmail: !!properties.$email,
        propertyKeys: Object.keys(updatedProperties)
      });
      
      mixpanelService.setUserProperties(updatedProperties);
      
      // Log without sensitive data
      const logSafeProperties = { ...properties };
      if (logSafeProperties.$email) logSafeProperties.$email = '[REDACTED]';
      if (logSafeProperties.$name) logSafeProperties.$name = '[REDACTED]';
      this.log.debug('User info updated with demographics', logSafeProperties);
    } else {
      this.log.warn('⚠️ [MIXPANEL EMAIL CHECK] NO properties to send - skipping Mixpanel update');
    }
  }

  /**
   * Get or create user analytics profile
   */
  getUserProfile(): UserAnalyticsProfile {
    const storageKey = `galos_profile_${this.anonymousId}`;
    const stored = localStorage.getItem(storageKey);
    
    if (stored) {
      try {
        const profile = JSON.parse(stored) as UserAnalyticsProfile;
        // Update last active
        profile.lastActiveAt = new Date().toISOString();
        if (this.userId && !profile.userId) {
          profile.userId = this.userId;
        }
        localStorage.setItem(storageKey, JSON.stringify(profile));
        return profile;
      } catch (error) {
        this.log.warn('Failed to parse stored user profile:', error);
      }
    }
    
    // Create new profile
    const newProfile: UserAnalyticsProfile = {
      anonymousId: this.anonymousId!,
      userId: this.userId || undefined,
      sessionId: this.sessionId!,
      templates: {},
      preferences: {
        favoriteCategories: [],
        avgSessionLength: 0
      },
      createdAt: new Date().toISOString(),
      lastActiveAt: new Date().toISOString()
    };
    
    localStorage.setItem(storageKey, JSON.stringify(newProfile));
    return newProfile;
  }

  /**
   * Update user profile data
   */
  updateUserProfile(updates: Partial<UserAnalyticsProfile>): void {
    const profile = this.getUserProfile();
    const updated = { ...profile, ...updates, lastActiveAt: new Date().toISOString() };
    
    const storageKey = `galos_profile_${this.anonymousId}`;
    localStorage.setItem(storageKey, JSON.stringify(updated));
  }

  /**
   * Track template usage in user profile
   */
  trackTemplateUsage(templateId: string, action: 'access' | 'run' | 'complete', metadata?: any): void {
    const profile = this.getUserProfile();
    
    if (!profile.templates[templateId]) {
      profile.templates[templateId] = {
        accessCount: 0,
        runCount: 0,
        avgRefinements: 0,
        avgCompletionTime: 0,
        lastUsed: new Date().toISOString()
      };
    }
    
    const templateData = profile.templates[templateId];
    templateData.lastUsed = new Date().toISOString();
    
    switch (action) {
      case 'access':
        templateData.accessCount++;
        break;
      case 'run':
        templateData.runCount++;
        break;
      case 'complete':
        if (metadata?.completionTime) {
          const currentAvg = templateData.avgCompletionTime;
          const runs = templateData.runCount;
          templateData.avgCompletionTime = ((currentAvg * (runs - 1)) + metadata.completionTime) / runs;
        }
        if (metadata?.refinements !== undefined) {
          const currentAvg = templateData.avgRefinements;
          const runs = templateData.runCount;
          templateData.avgRefinements = ((currentAvg * (runs - 1)) + metadata.refinements) / runs;
        }
        break;
    }
    
    this.updateUserProfile(profile);
  }

  /**
   * Generate UUID v4
   */
  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Reset user data (for logout)
   */
  reset(): void {
    this.userId = null;
    localStorage.removeItem('userId');
    mixpanelService.reset();
    
    // Generate new session and get fresh demographics
    this.sessionId = this.generateUUID();
    const demographics = getUserDemographicsForProfile();
    const fullDemographics = getUserDemographics();
    
    mixpanelService.identify(this.anonymousId!);
    mixpanelService.setUserProperties({
      anonymous_id: this.anonymousId,
      session_id: this.sessionId,
      user_type: 'anonymous',
      reset_at: new Date().toISOString(),
      
      // Fresh demographics for the anonymous session
      ...demographics,
      is_new_session: fullDemographics.is_new_session,
      viewport_width: fullDemographics.viewport_width,
      viewport_height: fullDemographics.viewport_height
    });
    
    this.log.debug('User tracking reset to anonymous');
  }
}

// Export singleton instance
export const userTrackingService = new UserTrackingService();
export default userTrackingService; 